const isPlainObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const deepMerge = (target, source) => {
  for (const key of Object.keys(source)) {
    const next = source[key];
    const current = target[key];
    // Only objects merge. Strings (the leaves) and arrays (vue-i18n's list interpolation
    // form) are REPLACED wholesale — merging an array element-wise would silently splice a
    // shorter override into a longer default and produce a half-translated list.
    target[key] = isPlainObject(next) && isPlainObject(current) ? deepMerge(current, next) : next;
  }
  return target;
};

/**
 * Deep-merge any number of vue-i18n message bundles into ONE NEW object.
 *
 * This is for an **app** assembling its `messages` from several files — shell strings plus a
 * per-feature file, or a base bundle plus a per-deployment override. A `packages/*` component
 * does not need it: its colocated `messages/` folder is one bundle read through a local
 * composer (see `useComponentI18n`), and a package must never depend on an app registering
 * anything (PACKAGING_PLAN §3.2).
 *
 * Shape in, shape out: `{ [locale]: { key: string | nested } }`.
 *
 * Rules:
 *   - **Later arguments win**, key by key and at any depth.
 *   - Two plain objects at the same path merge; anything else replaces — **strings and arrays
 *     are replaced, never merged**.
 *   - Every input is left untouched; nested objects are copied, not aliased, so a later write
 *     through the result cannot reach back into a source bundle.
 *   - Non-object arguments (`null`/`undefined`/a stray string) are skipped, so an optional
 *     bundle can be passed as `maybeBundle` without a guard at the call site.
 *
 * @param {...Record<string, object>} bundles Bundles in increasing precedence.
 * @returns {Record<string, object>} A new merged bundle.
 *
 * @example
 * mergeMessages(
 *   { en: { a: '1', nested: { x: 'x' } } },
 *   { en: { nested: { y: 'y' } }, th: { a: 'ก' } },
 * );
 * // => { en: { a: '1', nested: { x: 'x', y: 'y' } }, th: { a: 'ก' } }
 */
export function mergeMessages(...bundles) {
  const out = {};
  for (const bundle of bundles) {
    if (!isPlainObject(bundle)) continue;
    deepMerge(out, structuredCloneLike(bundle));
  }
  return out;
}

// A minimal deep copy for JSON-ish message data. `structuredClone` would also do it, but it
// throws on anything non-serialisable (a function, a Vue ref) — a message bundle that somehow
// carries one should degrade to a shared reference, not blow up the app's boot.
const structuredCloneLike = (value) => {
  if (Array.isArray(value)) return value.map(structuredCloneLike);
  if (!isPlainObject(value)) return value;
  const copy = {};
  for (const key of Object.keys(value)) copy[key] = structuredCloneLike(value[key]);
  return copy;
};
